import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Box, Flex, Grid, Text, Select, Input, InputGroup, InputLeftElement, Icon, Button, Spinner, useToast,
} from '@chakra-ui/react';
import { FiSearch, FiCheck, FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import {
  getActivation, previewActivation, applyActivation, ACTIVATION_STATE, errorMessage,
} from 'api/administrador/monitoring/aiGrading';

// Casilla de tres estados: marcada = queda activa, vacía = inactiva, guion = parcial (sin cambios).
function TriCheckbox({ state, label, onClick }) {
  const on = state === 'on';
  const mixed = state === 'mixed';
  return (
    <Box
      as="button" type="button" role="checkbox" aria-checked={on ? 'true' : mixed ? 'mixed' : 'false'} aria-label={label}
      onClick={onClick}
      w="22px" h="22px" flexShrink={0} borderRadius="6px" border="2px solid"
      borderColor={state === 'off' ? 'gray.400' : '#574feb'} bg={on ? '#574feb' : mixed ? '#EEF0FE' : 'white'}
      display="flex" alignItems="center" justifyContent="center"
    >
      {on && <Icon as={FiCheck} color="white" boxSize="14px" strokeWidth={3.5} />}
      {mixed && <Box w="10px" h="2px" borderRadius="2px" bg="#3730A3" />}
    </Box>
  );
}

function Stepper({ step }) {
  const item = (n, label) => {
    const active = step === n;
    const done = step > n;
    return (
      <Flex align="center" gap={2} fontSize="13px" fontWeight={active ? 600 : 400} color={active || done ? '#3730A3' : 'gray.500'}>
        <Flex
          w="22px" h="22px" borderRadius="999px" align="center" justify="center" fontSize="12px"
          bg={active ? '#574feb' : done ? '#EEF0FE' : 'white'} color={active ? 'white' : '#3730A3'}
          border={active || done ? 'none' : '1px solid #CBD5E0'}
        >
          {done ? <Icon as={FiCheck} boxSize="12px" /> : n}
        </Flex>
        {label}
      </Flex>
    );
  };
  return (
    <Flex as="ol" aria-label="Pasos" listStyleType="none" align="center" gap={2.5} mt={3}>
      <Box as="li">{item(1, 'Seleccionar')}</Box>
      <Box as="li" aria-hidden="true" w="40px" h="1px" bg={step > 1 ? '#574feb' : 'gray.300'} />
      <Box as="li">{item(2, 'Confirmar')}</Box>
    </Flex>
  );
}

function PreviewTable({ title, tone, group }) {
  if (!group.subjects.length) return null;
  const color = tone === 'on' ? '#047857' : '#B91C1C';
  const dot = tone === 'on' ? '#10B981' : '#EF4444';
  return (
    <Box>
      <Flex align="center" gap={2} mb={2.5} fontSize="14px" fontWeight="700" color={color}>
        <Box w="8px" h="8px" borderRadius="999px" bg={dot} />
        {title} · {group.subjects.length === 1 ? '1 materia' : `${group.subjects.length} materias`} · {group.count} actividades
      </Flex>
      <Box border="1px solid" borderColor="gray.200" borderRadius="12px" overflow="hidden" maxH="240px" overflowY="auto">
        <Grid templateColumns="1.4fr 1.6fr 0.8fr 0.8fr" px={3.5} py={2.5} bg="gray.50" fontSize="11px" fontWeight="600" letterSpacing=".06em" color="gray.600" textTransform="uppercase">
          <Text>Materia</Text><Text>Programa</Text><Text>Actividades</Text><Text>Entregas/sem.</Text>
        </Grid>
        {group.subjects.map((s) => (
          <Grid key={s.subjectId} templateColumns="1.4fr 1.6fr 0.8fr 0.8fr" px={3.5} py={2.5} borderTop="1px solid" borderColor="gray.100" fontSize="14px">
            <Text fontWeight="600">{s.name}</Text><Text color="gray.600">{s.program}</Text><Text>{s.activities}</Text><Text>{s.sittingsWeek}</Text>
          </Grid>
        ))}
      </Box>
    </Box>
  );
}

export default function ActivationModal({ isOpen, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [view, setView] = useState('subjects');
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [want, setWant] = useState({}); // key -> { state, orig, change }
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!isOpen) return;
    setStep(1); setWant({}); setPreview(null); setSearchText(''); setSearch('');
  }, [isOpen]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchText), 300);
    return () => clearTimeout(t);
  }, [searchText]);

  useEffect(() => {
    if (!isOpen || step !== 1) return;
    let cancelled = false;
    setLoading(true);
    getActivation({ view, search, pageSize: 200 })
      .then((res) => { if (!cancelled) setRows(res?.data || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, step, view, search]);

  const keyOf = (row) => (view === 'programs' ? `p:${row.key}` : `s:${row.id}`);
  const current = (row) => want[keyOf(row)]?.state || row.state;
  const flip = (row) => {
    const key = keyOf(row);
    const orig = row.state;
    const cur = current(row);
    // Activa → inactiva → (parcial, si lo era) → activa…
    const next = cur === 'on' ? 'off' : cur === 'off' && orig === 'mixed' ? 'mixed' : 'on';
    const change = view === 'programs' ? { type: 'program', key: row.key } : { type: 'subject', id: row.id };
    setWant({ ...want, [key]: { state: next, orig, change } });
  };

  const changes = useMemo(
    () => Object.values(want).filter((w) => w.state !== w.orig).map((w) => ({ ...w.change, enable: w.state === 'on' })),
    [want],
  );
  const toOn = changes.filter((c) => c.enable).length;
  const toOff = changes.length - toOn;

  const goPreview = async () => {
    setStep(2);
    setPreview(null);
    try {
      setPreview(await previewActivation(changes));
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e) });
      setStep(1);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await applyActivation(changes);
      const parts = [r.activated ? `${r.activated} activadas` : null, r.deactivated ? `${r.deactivated} desactivadas` : null].filter(Boolean);
      toast({ status: 'success', title: `Actividades ${parts.join(' · ') || 'sin cambios'}`, isClosable: true });
      onClose();
      if (onDone) onDone();
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  const nothing = preview && !preview.activate.count && !preview.deactivate.count;

  return (
    <Modal isOpen={isOpen} onClose={saving ? () => {} : onClose} size="3xl" scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="16px" maxH="880px">
        <ModalHeader borderBottom="1px solid" borderColor="gray.100" pb={4}>
          <Text fontSize="20px" fontWeight="700">{step === 1 ? 'Activar o desactivar calificación' : 'Revisa los cambios'}</Text>
          <Text fontSize="14px" color="gray.600" fontWeight="400" mt={1}>
            {step === 1
              ? 'Marca lo que debe quedar activo y desmarca lo que quieres desactivar.'
              : preview ? `${preview.activate.count + preview.deactivate.count} actividades cambian` : 'Calculando…'}
          </Text>
          <Stepper step={step} />
          {step === 1 && (
            <Flex gap={3} mt={4} fontWeight="400">
              <Flex as="label" align="center" gap={2} fontSize="13px" color="gray.600" flexShrink={0}>
                Ver por
                <Select size="md" w="150px" borderRadius="10px" value={view} onChange={(e) => { setView(e.target.value); setSearchText(''); }}>
                  <option value="programs">Programas</option>
                  <option value="subjects">Materias</option>
                </Select>
              </Flex>
              <InputGroup>
                <InputLeftElement pointerEvents="none"><Icon as={FiSearch} color="gray.400" /></InputLeftElement>
                <Input
                  aria-label={view === 'programs' ? 'Buscar programa' : 'Buscar materia'}
                  placeholder={view === 'programs' ? 'Buscar programa…' : 'Buscar materia…'}
                  value={searchText} onChange={(e) => setSearchText(e.target.value)} borderRadius="10px"
                />
              </InputGroup>
            </Flex>
          )}
        </ModalHeader>
        <ModalCloseButton isDisabled={saving} top={5} />

        <ModalBody px={step === 1 ? 0 : 6} py={step === 1 ? 0 : 5}>
          {step === 1 && (
            <>
              {loading && !rows.length && <Flex justify="center" py={10}><Spinner color="#574feb" /></Flex>}
              {!loading && !rows.length && <Text textAlign="center" py={10} color="gray.500" fontSize="sm">No hay resultados.</Text>}
              {rows.map((row) => {
                const key = keyOf(row);
                const w = want[key];
                const changed = w && w.state !== w.orig;
                const enable = w?.state === 'on';
                const affected = enable ? row.activities.total - row.activities.on : row.activities.on;
                return (
                  <Flex key={key} align="center" gap={3.5} px={6} py={3} borderBottom="1px solid" borderColor="gray.100" bg={changed ? (enable ? '#F0FDF4' : '#FEF2F2') : 'white'}>
                    <TriCheckbox state={current(row)} label={row.name} onClick={() => flip(row)} />
                    <Box flex={1} minW={0}>
                      <Text fontSize="14px" fontWeight="600" isTruncated>{row.name}</Text>
                      <Text fontSize="12px" color="gray.500" isTruncated>
                        {view === 'programs' ? `${row.type} · ${row.subjects.length} materias` : `${row.program.name}${row.program.type ? ` · ${row.program.type}` : ''}`}
                      </Text>
                    </Box>
                    <Text fontSize="13px" color="gray.600" w="110px" textAlign="right">{row.activities.on} de {row.activities.total} act.</Text>
                    <Flex w="160px" justify="flex-end">
                      {changed ? (
                        <Box px={2.5} py={1} borderRadius="999px" fontSize="12px" fontWeight="600"
                          bg={enable ? '#ECFDF5' : '#FEF2F2'} color={enable ? '#047857' : '#B91C1C'} border={`1px solid ${enable ? '#A7F3D0' : '#FECACA'}`}>
                          {enable ? 'Se activa' : 'Se desactiva'} · {affected} act.
                        </Box>
                      ) : (
                        <Text fontSize="12px" color="gray.500">Hoy: {ACTIVATION_STATE[row.state]?.label}</Text>
                      )}
                    </Flex>
                  </Flex>
                );
              })}
            </>
          )}
          {step === 2 && (
            <Flex direction="column" gap={5}>
              {!preview && <Flex justify="center" py={10}><Spinner color="#574feb" /></Flex>}
              {nothing && <Text fontSize="sm" color="gray.600">Con esa selección no cambia ninguna actividad.</Text>}
              {preview && <PreviewTable title="Se activan" tone="on" group={preview.activate} />}
              {preview && <PreviewTable title="Se desactivan" tone="off" group={preview.deactivate} />}
              {preview?.sharedActivities > 0 && (
                <Text fontSize="13px" color="gray.600">
                  {preview.sharedActivities === 1 ? '1 actividad se usa' : `${preview.sharedActivities} actividades se usan`} también en otras materias: el cambio aplica allí también.
                </Text>
              )}
            </Flex>
          )}
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="gray.100" justifyContent="space-between">
          {step === 1 ? (
            <>
              <Flex gap={2} fontSize="13px">
                <Box px={2.5} py={1} borderRadius="999px" bg="#ECFDF5" color="#047857" border="1px solid #A7F3D0" fontWeight="600">{toOn} por activar</Box>
                <Box px={2.5} py={1} borderRadius="999px" bg="#FEF2F2" color="#B91C1C" border="1px solid #FECACA" fontWeight="600">{toOff} por desactivar</Box>
              </Flex>
              <Flex gap={2.5}>
                <Button variant="outline" onClick={onClose} borderRadius="10px">Cancelar</Button>
                <Button rightIcon={<FiArrowRight />} onClick={goPreview} isDisabled={!changes.length} bg="#574feb" color="white" _hover={{ bg: '#4338CA' }} borderRadius="10px">
                  Continuar
                </Button>
              </Flex>
            </>
          ) : (
            <>
              <Button leftIcon={<FiArrowLeft />} variant="outline" onClick={() => setStep(1)} isDisabled={saving} borderRadius="10px">Atrás</Button>
              <Button onClick={save} isLoading={saving} isDisabled={!preview || nothing} bg="#574feb" color="white" _hover={{ bg: '#4338CA' }} borderRadius="10px" px={6}>
                Guardar
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
